import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiDownload, FiArrowRight, FiRefreshCw, FiUsers } from 'react-icons/fi';
import {
  generateHandoverSummary,
  downloadHandoverDocx,
  continueFromHandover,
} from '../utils/conversationApi';
import './HandoverModal.css';

const CLOSE_ANIMATION_MS = 220;

function Section({ heading, children }) {
  return (
    <section className="handover-section">
      <h3>{heading}</h3>
      {children}
    </section>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <p className="handover-muted">None.</p>;
  return (
    <ul>
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

export default function HandoverModal({
  isOpen,
  onClose,
  conversationId,
  conversationName,
  onContinued,
  // A group chat's handover: the new chat can be the user's own, or a new
  // group chat for the same people -- they choose.
  groupChat = false,
  // Who a new group chat would be for, e.g. "everyone in this project" --
  // said when asking them to confirm it.
  groupAudience = '',
}) {
  const [isClosing, setIsClosing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // false, or which new chat is being started: 'mine' or 'group'.
  const [continuing, setContinuing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /** Shows the "Plain / Letterhead template" question when the user clicks Download. */
  const [formatPromptOpen, setFormatPromptOpen] = useState(false);
  /** Asking "are you sure?" before starting a new group chat: everyone in it
   * sees it at once, so a mistaken one confuses the whole project. */
  const [confirmGroup, setConfirmGroup] = useState(false);
  const modalRef = useRef(null);
  const confirmRef = useRef(null);
  const confirmBackdropRef = useRef(null);
  const groupButtonRef = useRef(null);
  const confirmWasOpenRef = useRef(false);
  /** True until the user opts in — opening the modal does not call the API. */
  const [awaitingUserStart, setAwaitingUserStart] = useState(true);

  const loadSummary = useCallback(
    async ({ force = false } = {}) => {
      if (!conversationId) return;
      setError('');
      if (force) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const data = await generateHandoverSummary(conversationId, { force });
        setSummary(data);
      } catch (err) {
        setError(err.message || 'Failed to generate handover summary.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [conversationId]
  );

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setSummary(null);
      setError('');
      setLoading(false);
      setRefreshing(false);
      setAwaitingUserStart(true);
      setFormatPromptOpen(false);
    }
    // Closed, including after a continuation that started from it, the
    // pop-up goes too, so its Tab trap doesn't outlive the handover.
    setConfirmGroup(false);
  }, [isOpen]);

  // "Start a new group chat?" is the only thing usable while it's up:
  // Escape answers Cancel (unless it's already starting), and Tab goes
  // round its own buttons instead of reaching the handover underneath.
  useEffect(() => {
    if (!isOpen || !confirmGroup) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !continuing) {
        e.stopPropagation();
        setConfirmGroup(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const box = confirmRef.current;
      const buttons = box ? Array.from(box.querySelectorAll('button:not([disabled])')) : [];
      e.preventDefault();
      if (!buttons.length) return;
      const at = buttons.indexOf(document.activeElement);
      const next = at === -1
        ? (e.shiftKey ? buttons.length - 1 : 0)
        : (at + (e.shiftKey ? -1 : 1) + buttons.length) % buttons.length;
      buttons[next].focus();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, confirmGroup, continuing]);

  // While it's up, everything behind it in the handover is inert -- out of
  // reach of the keyboard, the mouse and screen readers. When it goes,
  // focus goes back to the button that opened it.
  useEffect(() => {
    const modal = modalRef.current;
    if (modal) {
      Array.from(modal.children).forEach((child) => {
        if (child !== confirmBackdropRef.current) child.inert = confirmGroup;
      });
    }
    if (confirmGroup) {
      confirmWasOpenRef.current = true;
    } else if (confirmWasOpenRef.current) {
      confirmWasOpenRef.current = false;
      if (groupButtonRef.current && !groupButtonRef.current.disabled) groupButtonRef.current.focus();
    }
  }, [confirmGroup]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (continuing || downloading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  };

  const handleDownload = async (format = 'plain') => {
    if (!conversationId) return;
    setDownloading(true);
    setError('');
    try {
      // Capitalized to match the backend's Content-Disposition name, which
      // normally wins -- this hint is only the fallback if that header is absent.
      const prefix = format === 'letterhead' ? 'Letterhead' : 'Handover';
      await downloadHandoverDocx(
        conversationId,
        `${prefix}_${conversationName || 'conversation'}.docx`,
        format
      );
      setFormatPromptOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to download handover document.');
    } finally {
      setDownloading(false);
    }
  };

  const handleContinue = async (asGroup = false) => {
    if (!conversationId) return;
    setContinuing(asGroup ? 'group' : 'mine');
    setError('');
    try {
      const payload = await continueFromHandover(conversationId, { asGroup });
      const newConversation = payload?.conversation;
      if (!newConversation) {
        throw new Error('Backend did not return the new conversation.');
      }
      if (typeof onContinued === 'function') {
        await onContinued(newConversation);
      }
      setIsClosing(true);
      setTimeout(() => onClose(), CLOSE_ANIMATION_MS);
    } catch (err) {
      setError(err.message || 'Failed to start the continued conversation.');
      setConfirmGroup(false);
    } finally {
      setContinuing(false);
    }
  };

  const showLoading = loading && !summary;
  const showIntro = awaitingUserStart && !summary && !loading && !error;

  const handleStartSummarizing = () => {
    setAwaitingUserStart(false);
    loadSummary();
  };

  // On the page itself, so the app's header bar can't sit over its top.
  return createPortal(
    <div
      className={`handover-modal-overlay ${isClosing ? 'handover-modal-overlay--closing' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`handover-modal ${isClosing ? 'handover-modal--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        ref={modalRef}
      >
        <div className="handover-modal-header">
          <div className="handover-modal-header-text">
            <h2>Hand over this conversation</h2>
            <p className="handover-modal-subtitle">
              {conversationName ? `"${conversationName}"` : 'Long conversations get easier to manage with a summary.'}
            </p>
          </div>
          <div className="handover-modal-header-actions">
            {summary && (
              <button
                type="button"
                className="handover-modal-icon-btn"
                onClick={() => loadSummary({ force: true })}
                disabled={loading || refreshing || continuing || downloading}
                title="Regenerate summary"
                aria-label="Regenerate summary"
              >
                <FiRefreshCw size={16} className={refreshing ? 'handover-spin' : ''} />
              </button>
            )}
            <button
              type="button"
              className="handover-modal-icon-btn"
              onClick={handleClose}
              aria-label="Close"
              disabled={continuing || downloading}
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        <div className="handover-modal-body">
          {showIntro ? (
            <div className="handover-intro">
              <p className="handover-intro-lead">
                A <strong>handover</strong> is a written summary of this chat: what you are trying to achieve, what
                was covered, and where things left off. It helps when threads get long or you want a clean slate
                without losing the substance of the work.
              </p>
              {groupChat ? (
                <p>You can <strong>download a Word document</strong> to keep or share, and/or start a new chat
                  seeded with this summary that links back to this one: <strong>just for you</strong>, or{' '}
                  <strong>a new group chat</strong> with the same people.
                </p>
              ) : (
                <p>You can <strong>download a Word document</strong> to keep or share, and/or{' '}
                  <strong>start a new conversation</strong> that is seeded with this summary and links back to this one.
                </p>
              )}
              <p className="handover-intro-cost">
                Generating the summary runs an AI on the full thread and can take from a few seconds to a couple of
                minutes for very large chats. Nothing runs until you choose to start.
              </p>
              <div className="handover-intro-action">
                <button
                  type="button"
                  className="handover-btn handover-btn-primary"
                  onClick={handleStartSummarizing}
                  disabled={!conversationId}
                >
                  Start summarizing the conversation
                </button>
              </div>
            </div>
          ) : showLoading ? (
            <div className="handover-loading">
              <div className="handover-spinner" />
              <p>Summarising the conversation in one pass. This can take 30–120 seconds for long or detailed chats.</p>
            </div>
          ) : error && !summary ? (
            <div className="handover-error">
              <p>{error}</p>
              <button type="button" className="handover-btn handover-btn-secondary" onClick={() => loadSummary()}>
                Try again
              </button>
            </div>
          ) : summary ? (
            <div className="handover-preview">
              <div className="handover-preview-title">
                <h3>{summary.title || 'Conversation handover'}</h3>
                {summary.generated_at && (
                  <span className="handover-preview-timestamp">
                    Generated {new Date(summary.generated_at).toLocaleString()}
                  </span>
                )}
              </div>
              {refreshing && <div className="handover-refreshing-note">Regenerating…</div>}

              {summary.user_goal && (
                <Section heading="User goal">
                  <p>{summary.user_goal}</p>
                </Section>
              )}
              {summary.current_state && (
                <Section heading="Current state">
                  <p>{summary.current_state}</p>
                </Section>
              )}
              <Section heading="Topics covered">
                <BulletList items={summary.topics_covered} />
              </Section>
              <Section heading="Key decisions">
                <BulletList items={summary.key_decisions} />
              </Section>
              <Section heading="Artifacts referenced">
                <BulletList items={summary.artifacts_referenced} />
              </Section>
              <Section heading="Open questions">
                <BulletList items={summary.open_questions} />
              </Section>
              <Section heading="Recommended next steps">
                <BulletList items={summary.recommended_next_steps} />
              </Section>

              <p className="handover-fineprint">
                Detail from the original chat is condensed. The original conversation is preserved — the new chat will
                link back to it.
              </p>
              {error && <p className="handover-inline-error">{error}</p>}
            </div>
          ) : null}
        </div>

        <div className="handover-modal-footer">
          {formatPromptOpen ? (
            <div className="handover-format-prompt">
              <span className="handover-format-prompt-label">Which format?</span>
              <button
                type="button"
                className="handover-btn handover-btn-secondary"
                onClick={() => handleDownload('plain')}
                disabled={downloading}
              >
                Plain
              </button>
              <button
                type="button"
                className="handover-btn handover-btn-secondary"
                onClick={() => handleDownload('letterhead')}
                disabled={downloading}
              >
                Letterhead template
              </button>
              <button
                type="button"
                className="handover-modal-icon-btn"
                onClick={() => setFormatPromptOpen(false)}
                disabled={downloading}
                aria-label="Cancel"
                title="Cancel"
              >
                <FiX size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="handover-btn handover-btn-secondary"
              onClick={() => setFormatPromptOpen(true)}
              disabled={!summary || downloading || continuing || loading}
            >
              <FiDownload size={14} style={{ marginRight: 6 }} />
              {downloading ? 'Preparing…' : 'Download .docx'}
            </button>
          )}
          {groupChat && (
            <button
              type="button"
              className="handover-btn handover-btn-secondary"
              onClick={() => handleContinue(false)}
              disabled={!summary || Boolean(continuing) || downloading || loading}
              title="A new conversation of your own in this project, seeded with the summary."
            >
              {continuing === 'mine' ? 'Starting…' : 'Continue on my own'}
            </button>
          )}
          <button
            type="button"
            className="handover-btn handover-btn-primary"
            ref={groupButtonRef}
            onClick={() => (groupChat ? setConfirmGroup(true) : handleContinue(false))}
            disabled={!summary || Boolean(continuing) || downloading || loading}
            title={groupChat ? 'A new group chat with the same people, seeded with the summary.' : undefined}
          >
            {continuing && continuing === (groupChat ? 'group' : 'mine') ? 'Starting new chat…' : (
              <>
                {groupChat ? 'Continue as a group chat' : 'Start new chat from summary'}
                <FiArrowRight size={14} style={{ marginLeft: 6 }} />
              </>
            )}
          </button>
        </div>

        {/* "Start a new group chat?" -- a small pop-up over the handover:
            everyone in it sees the new chat at once, so it asks first. */}
        {confirmGroup && (
          <div className="handover-confirm-backdrop" ref={confirmBackdropRef}>
            <div className="handover-confirm" role="alertdialog" aria-modal="true" ref={confirmRef}
              aria-labelledby="handover-confirm-title" aria-describedby="handover-confirm-text">
              <span className="handover-confirm-icon" aria-hidden="true"><FiUsers size={20} /></span>
              <p id="handover-confirm-title" className="handover-confirm-title">Start a new group chat?</p>
              <p id="handover-confirm-text" className="handover-confirm-text">
                For {groupAudience || 'the same people'}. It shows up in the project&apos;s Group chats for all
                of them straight away.
              </p>
              <p className="handover-confirm-text">
                This chat then closes: everyone can still read it, but new questions go in the new chat.
              </p>
              <div className="handover-confirm-actions">
                <button
                  type="button"
                  className="handover-btn handover-btn-secondary"
                  onClick={() => setConfirmGroup(false)}
                  disabled={Boolean(continuing)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="handover-btn handover-btn-primary"
                  onClick={() => handleContinue(true)}
                  disabled={Boolean(continuing)}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                >
                  {continuing ? 'Starting new chat…' : 'Yes, start group chat'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
